import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import {
    CreateCableModalComponent,
    CreateCableModalData,
} from '@connect-services/client/components/modals/create-cable-modal/create-cable-modal.component';
import { CreateCassetteModalComponent } from '@connect-services/client/components/modals/create-cassette-modal/create-cassette-modal.component';
import { SynopticPlanItem } from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import {
    CreateDrawerModalComponent,
    CreateDrawerModalData,
    CreateDrawerModalResult,
} from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/create-drawer-modal/create-drawer-modal.component';
import {
    PositionEditModalComponent,
    PositionEditModalData,
    PositionEditModalResult,
} from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/position-edit-modal/position-edit-modal.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import {
    CableByUsage,
    CableDirectionEnum,
    CassetteReferencePosition,
    SynopticBoxPlanCable,
    SynopticBoxPlanCableBySide,
    SynopticPositionListValues,
} from '@connect-services/client/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class SynopticPlanEditModalService {
    private readonly matDialog = inject(MatDialog);
    private readonly translocoService = inject(TranslocoService);

    public openCreateCableModal(
        cables: SynopticBoxPlanCableBySide,
        deletedCableKeys: Set<string>,
        ids: { boxBay: string; deposit: string; editSession: string },
        side: CableDirectionEnum,
    ): Promise<CableByUsage[] | null | undefined> {
        const currentCableKeys = Object.values(cables)
            .flat()
            .map((cable: SynopticBoxPlanCable) => cable.label ?? '');

        const dialogRef = this.matDialog.open<CreateCableModalComponent, CreateCableModalData, CableByUsage[] | null>(
            CreateCableModalComponent,
            {
                autoFocus: false,
                data: {
                    currentBoxBayId: ids.boxBay,
                    currentCables: new Set(currentCableKeys),
                    deletedCables: deletedCableKeys,
                    depositId: ids.deposit,
                    editSessionId: ids.editSession,
                    isEntryElement: side === CableDirectionEnum.LEFT,
                },
                minWidth: '20vw',
            },
        );

        return firstValueFrom(dialogRef.afterClosed());
    }

    public openCreateCassetteModal(depositId: string): Promise<CassetteReferencePosition | null> {
        const dialogRef = this.matDialog.open(CreateCassetteModalComponent, {
            autoFocus: false,
            data: { depositId: depositId },
            minWidth: '20vw',
        });

        return firstValueFrom(dialogRef.afterClosed());
    }

    public openCreateDrawerModal(depositId: string): Promise<CreateDrawerModalResult | null | undefined> {
        const dialogRef = this.matDialog.open<
            CreateDrawerModalComponent,
            CreateDrawerModalData,
            CreateDrawerModalResult
        >(CreateDrawerModalComponent, {
            data: { depositId: depositId },
            width: '30vw',
        });

        return firstValueFrom(dialogRef.afterClosed());
    }

    public async openLeaveEditionConfirmModal(): Promise<boolean> {
        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('quit'),
                description: this.translocoService.translate('unsavedEditsWillBeLost'),
                title: this.translocoService.translate('leavePage'),
                type: ModalType.DANGER,
            },
            width: '35rem',
        });

        const modalResult = await firstValueFrom(dialogRef.afterClosed());

        return modalResult === ModalResultEnum.CONFIRM;
    }

    public openPositionEditModal(
        positionListValues: SynopticPositionListValues,
        fiber?: SynopticPlanItem,
    ): Promise<PositionEditModalResult | undefined> {
        const dialogRef = this.matDialog.open<
            PositionEditModalComponent,
            PositionEditModalData,
            PositionEditModalResult
        >(PositionEditModalComponent, {
            data: { fiber: fiber, positionListValues: positionListValues },
            width: '30vw',
        });

        return firstValueFrom(dialogRef.afterClosed());
    }
}
